import { Keypair } from '@solana/web3.js'
import { atom, Getter, Setter } from 'jotai'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'

import { auth } from '../data/FirebaseInit'
import { db } from '../data/FirebaseUtils'
import { DRAFT_DROP_TIME_QUERY, indexerClient } from '../graphql/graphql-queries'
import { Query_Root } from '../graphql/indexer-graphql-types'
import {
  isUserVerified,
  draftScheduleTimeAtom,
  listenToTranscodingStatus,
  mintAtom,
  rSubStepInDetail,
  rwIsTranscodingFinished,
  rwUploadFileProgressAtom,
  rwUploadStepAtom,
  CreateSteps,
  videoFileInfoAtom,
  videoMetadataAtom,
  isUserVerifiedAtom,
  monetizationDetailsAtom,
  AccessTypes,
  uploadTypeAtom,
} from './Creation'
import { Whitelist } from '../data/Whitelist'

export enum CREATOR_CHECK_STEP {
  NONE,
  CANCELLED,
  CHECKING_WHITELIST,
  NOT_WHITELISTED,
  CHECKING_PENDING_CREATION,
}

export const rwCreatorCheckStepAtom = atom<CREATOR_CHECK_STEP>(CREATOR_CHECK_STEP.NONE)

export const wCheckKYCStatusAtom = atom(null, (_, set) => {
  ;(async (): Promise<void> => {
    const isVerified = await isUserVerified()
    set(isUserVerifiedAtom, isVerified)
  })()
})

export const wCheckCreatorWhitelistAtom = atom(null, (_, set, secret: string) => {
  ;(async (): Promise<void> => {
    set(rwCreatorCheckStepAtom, CREATOR_CHECK_STEP.CHECKING_WHITELIST)

    const address = auth.currentUser?.uid
    const whitelistDoc = db.whitelist.doc(address)
    const whitelistData = await whitelistDoc.get()
    if (whitelistData.exists) {
      set(wInitCreate)
      return
    }

    if (secret) {
      const secretDoc = db.secretLinks.doc(secret)
      const secretData = await secretDoc.get()
      if (secretData.exists) {
        await Whitelist()
        set(wInitCreate)
        return
      }
    }

    set(rwCreatorCheckStepAtom, CREATOR_CHECK_STEP.NOT_WHITELISTED)
    const unsubscribe = whitelistDoc.onSnapshot((doc: any) => {
      if (doc.data()) {
        set(wInitCreate)
        unsubscribe()
      }
    })
  })()
})

const clearCreationAtoms = (set: Setter): void => {
  set(rwCreatorCheckStepAtom, CREATOR_CHECK_STEP.NONE)
  set(rwUploadStepAtom, CreateSteps.ACCESS)
  set(rwIsTranscodingFinished, false)
}

const wInitCreate = atom(null, (_get, set) => {
  set(rwCreatorCheckStepAtom, CREATOR_CHECK_STEP.CHECKING_PENDING_CREATION)
  const address = auth.currentUser?.uid
  if (!address) {
    set(rwCreatorCheckStepAtom, CREATOR_CHECK_STEP.CANCELLED)
    return
  }

  clearCreationAtoms(set)
})

export const wResumePendingVideo = atom(null, (get, set, videoAddress: string) => {
  const resume = async (): Promise<void> => {
    clearCreationAtoms(set)

    const doc = await db.videos.doc(videoAddress).get()
    const pendingVideo = {
      id: doc.id,
      ...doc.data(),
    }

    checkPendingVideo(pendingVideo, get, set)
  }

  resume()
})

const checkPendingVideo = async (pendingVideo: any, get: Getter, set: Setter): Promise<void> => {
  const address = auth.currentUser?.uid
  if (!address) {
    set(rwCreatorCheckStepAtom, CREATOR_CHECK_STEP.CANCELLED)
    return
  }

  if (pendingVideo) {
    const { video_uuid: uuid, title, description, price, max_editions, min_editions } = pendingVideo
    const transcoding = (await db.transcoding.doc(uuid).get()).data()
    if (transcoding) {
      const {
        duration,
        width,
        height,
        video_transcoding_status,
        video_uri,
        trailer_uri,
        thumbnail_uri,
        gated,
      } = transcoding
      set(videoMetadataAtom, {
        title,
        description,
        // poster will be used for nothing but checking whether poster exists or not for metadata validation
        poster:
          thumbnail_uri &&
          new URL(
            `https://glassimg.imgix.net/${thumbnail_uri}?ar=16:9&fit=crop&fm=webp&w=1200&h=675`
          ),
      })
      set(videoFileInfoAtom, {
        url: video_uri,
        trailer: trailer_uri,
        thumbnail: thumbnail_uri,
        type: 'application/vnd.apple.mpegurl',
      })
      set(uploadTypeAtom, gated ? AccessTypes.GATED : AccessTypes.FREE)

      const { private_key: secretKey } = (
        await db.secrets(address).doc(pendingVideo.id).get()
      ).data() as { private_key: string }

      const keypair = Keypair.fromSecretKey(
        new Uint8Array(secretKey.split(',').map((c: string) => Number(c)))
      )

      set(mintAtom, keypair)

      // 0 should be excluded here because free mints will have prices of value of 0
      if (price !== undefined && (max_editions !== undefined || min_editions !== undefined)) {
        set(monetizationDetailsAtom, {
          pricePerEdition: price / LAMPORTS_PER_SOL,
          minNumberOfEditions: min_editions,
          maxNumberOfEditions: max_editions / LAMPORTS_PER_SOL,
        })
      }

      await checkDraftScheduleTime(keypair.publicKey.toString(), get, set)

      const listenToTranscoding = (): void => {
        listenToTranscodingStatus(pendingVideo.id, get, set)
        checkSubstepInDetail(CreateSteps.PROCESSING, get, set)
      }

      if (video_transcoding_status === 'succeeded') {
        set(rwIsTranscodingFinished, true)
        set(rwUploadFileProgressAtom, 100)

        if (video_uri) {
          checkSubstepInDetail(CreateSteps.MINT, get, set)
        } else {
          // this is when mux video uploaded but video is not on arweave yet
          listenToTranscoding()
        }
      } else if (video_transcoding_status === 'failed') {
        // do nothing, so users will be taken to upload step.
        // we are not showing CreateSteps.PROCESSING_FAILED here
        // because we don't want users see this again when they refresh the page
      } else if (['processing', ''].includes(video_transcoding_status)) {
        // Note ^: at the very beginning, transcoding_status is '', which is equivalent to 'processing'
        if (!duration || !height || !width) {
          // Still uploading!!!
          listenToTranscoding()
        } else {
          set(rwUploadFileProgressAtom, 100)

          listenToTranscoding()
        }
      }
    } else {
      /*      
      // listen to pendingVideo until video_uuid shows up, i.e, until the video uploading is done
      set(rwUploadStepAtom, CreateSteps.PROCESSING)
      const unsubscribe = db.videos.doc(pendingVideo.id).onSnapshot((doc: any) => {
        if (doc.data().video_uuid) {
          // video_uuid found, thus, initiating this recursive call to start the process again
          checkPendingVideo(pendingVideo, get, set)
          unsubscribe()
        }
      })
      */
    }
  } else {
    set(rwUploadStepAtom, CreateSteps.ACCESS)
  }
}

const checkDraftScheduleTime = async (mint: string, _: Getter, set: Setter): Promise<void> => {
  const dropTimeData: Query_Root = (
    await indexerClient.query({
      query: DRAFT_DROP_TIME_QUERY,
      fetchPolicy: 'no-cache',
      variables: {
        mint,
      },
    })
  ).data
  const dropTime = dropTimeData?.dropTimes[0]?.dropTime
  const scheduleTime = dropTime ? new Date(dropTime) : undefined

  set(draftScheduleTimeAtom, scheduleTime)
}

const checkSubstepInDetail = (nextStep: CreateSteps, get: Getter, set: Setter): void => {
  const subStepInDetail = get(rSubStepInDetail)
  if (subStepInDetail) {
    set(rwUploadStepAtom, subStepInDetail)
  } else {
    set(rwUploadStepAtom, nextStep)
  }
}
